import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useStore } from "@/contexts/StoreContext";
import { createManualOrder, getApiErrorMessage } from "@/lib/api";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialForm = {
    email: "",
    mailingListOptIn: false,
    firstName: "",
    lastName: "",
    streetAddress: "",
    apartment: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
    phoneNumber: "",
    testCardNumber: "",
    testExpiry: "",
    testThreeDigit: "",
    testFourDigit: "",
};

const Checkout = () => {
    const { cart, cartTotal, clearCart } = useStore();
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const itemCount = useMemo(
        () => cart.reduce((sum, item) => sum + item.quantity, 0),
        [cart]
    );

    const exactDemoValue = (value) => {
        const trimmed = value.trim();
        return trimmed || null;
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const validateForm = () => {
        if (cart.length === 0) {
            return "Cart is empty.";
        }
        if (!EMAIL_PATTERN.test(form.email)) {
            return "Enter a valid email address.";
        }

        const requiredFields = [
            ["firstName", "first name"],
            ["lastName", "last name"],
            ["streetAddress", "street address"],
            ["city", "city"],
            ["state", "state"],
            ["zipCode", "zip code"],
            ["country", "country"],
            ["phoneNumber", "phone number"],
        ];

        const missingField = requiredFields.find(([key]) => !form[key].trim());
        if (missingField) {
            return `Enter your ${missingField[1]}.`;
        }

        return null;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const error = validateForm();
        if (error) {
            toast.error(error);
            return;
        }

        setLoading(true);
        try {
            const order = await createManualOrder({
                email: form.email.trim(),
                mailing_list_opt_in: form.mailingListOptIn,
                billing_address: {
                    first_name: form.firstName.trim(),
                    last_name: form.lastName.trim(),
                    street_address: form.streetAddress.trim(),
                    apartment: form.apartment.trim() || null,
                    city: form.city.trim(),
                    state: form.state.trim(),
                    zip_code: form.zipCode.trim(),
                    country: form.country.trim(),
                    phone_number: form.phoneNumber.trim(),
                },
                demo_payment: {
                    test_card_number: exactDemoValue(form.testCardNumber),
                    test_expiry: exactDemoValue(form.testExpiry),
                    test_three_digit: exactDemoValue(form.testThreeDigit),
                    test_four_digit: exactDemoValue(form.testFourDigit),
                },
                gift_receipt: false,
                gift_message: null,
                items: cart.map((item) => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    size: item.size,
                    color: item.color,
                    custom_amount: item.customAmount,
                })),
            });

            clearCart();
            navigate(`/checkout/success?order=${encodeURIComponent(order.order_id)}`, {
                replace: true,
            });
        } catch (errorResponse) {
            toast.error(getApiErrorMessage(errorResponse, "Could not submit your order. Try again."));
            setLoading(false);
        }
    };

    if (cart.length === 0) {
        return (
            <div className="px-4 py-24 text-center md:px-8" data-testid="checkout-empty">
                <p className="font-heading text-5xl uppercase">Bag is empty.</p>
                <Link
                    to="/shop"
                    className="mt-8 inline-block border-2 border-black bg-black px-8 py-4 font-mono-ui text-xs uppercase tracking-[0.25em] text-white transition-colors hover:border-hazard hover:bg-hazard hover:text-black"
                >
                    Shop The Drop
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="checkout-page">
            <section className="border-b-4 border-black px-4 py-10 md:px-8 md:py-16">
                <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-hazard">
                    [Checkout / Step 02 of 02]
                </span>
                <h1 className="mt-2 font-heading text-6xl uppercase leading-[0.85] md:text-8xl">
                    Checkout
                </h1>
                <p className="mt-3 max-w-3xl font-mono-ui text-xs uppercase tracking-[0.18em] text-muted-foreground">
                    Purchase your clothing
                </p>
            </section>

            <section className="grid grid-cols-1 border-b-4 border-black lg:grid-cols-12">
                <form
                    onSubmit={handleSubmit}
                    className="border-black p-6 md:p-10 lg:col-span-7 lg:border-r-4 lg:p-12"
                >
                    <FormSection title="Confirmation Email">
                        <Field
                            label="Email Address"
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                        />
                        <label className="mt-5 flex items-start gap-3 font-mono-ui text-xs uppercase tracking-[0.16em] text-foreground">
                            <input
                                type="checkbox"
                                name="mailingListOptIn"
                                checked={form.mailingListOptIn}
                                onChange={handleChange}
                                className="mt-0.5 h-4 w-4 accent-black"
                            />
                            Sign me up to the mailing list
                        </label>
                    </FormSection>

                    <FormSection title="Billing Address" className="mt-12">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Field label="First Name" name="firstName" value={form.firstName} onChange={handleChange} />
                            <Field label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} />
                        </div>
                        <Field
                            label="Street Address"
                            name="streetAddress"
                            value={form.streetAddress}
                            onChange={handleChange}
                            className="mt-4"
                        />
                        <Field
                            label="Apt/Suite/Buzzer/etc"
                            name="apartment"
                            value={form.apartment}
                            onChange={handleChange}
                            className="mt-4"
                            required={false}
                        />
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Field label="City" name="city" value={form.city} onChange={handleChange} />
                            <Field label="State" name="state" value={form.state} onChange={handleChange} />
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Field label="Zip Code" name="zipCode" value={form.zipCode} onChange={handleChange} />
                            <Field label="Country" name="country" value={form.country} onChange={handleChange} />
                        </div>
                        <Field
                            label="Phone Number"
                            name="phoneNumber"
                            value={form.phoneNumber}
                            onChange={handleChange}
                            className="mt-4"
                        />
                    </FormSection>

                    <FormSection title="Payment" className="mt-12">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <DemoField
                                label="Card Number"
                                name="testCardNumber"
                                value={form.testCardNumber}
                                onChange={handleChange}
                                required={false}
                                placeholder="4111111111111111"
                            />
                            <DemoField
                                label="Expiry"
                                name="testExpiry"
                                value={form.testExpiry}
                                onChange={handleChange}
                                required={false}
                                placeholder="12/34"
                            />
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <DemoField
                                label="CVV"
                                name="testThreeDigit"
                                value={form.testThreeDigit}
                                onChange={handleChange}
                                required={false}
                                placeholder="123"
                            />
                            <DemoField
                                label="Pin Code"
                                name="testFourDigit"
                                value={form.testFourDigit}
                                onChange={handleChange}
                                required={false}
                                placeholder="0000"
                            />
                        </div>
                        
                    </FormSection>

                    <button
                        type="submit"
                        disabled={loading}
                        className="mt-10 w-full border-2 border-black bg-black py-5 font-mono-ui text-xs uppercase tracking-[0.3em] text-white transition-colors hover:border-hazard hover:bg-hazard hover:text-black disabled:opacity-50"
                        data-testid="checkout-pay-button"
                    >
                        {loading ? "Submitting…" : "Submit Order Request"}
                    </button>

                    <button
                        type="button"
                        onClick={() => navigate("/cart")}
                        className="mt-3 w-full py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] hover:text-hazard"
                        data-testid="checkout-back-link"
                    >
                        ← Back to Bag
                    </button>
                </form>

                <aside className="bg-black p-6 text-white md:p-10 lg:col-span-5 lg:p-12">
                    <h2 className="mb-4 font-mono-ui text-xs uppercase tracking-[0.25em] text-white/75">
                        // Order Summary ({itemCount})
                    </h2>
                    <p className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-white/60">
                        Manual processing checkout
                    </p>
                    <div className="mt-6 max-h-80 space-y-3 overflow-y-auto pr-2">
                        {cart.map((item) => (
                            <div
                                key={item.key}
                                className="flex gap-3 border-2 border-black bg-white p-3 text-black"
                                data-testid={`checkout-line-${item.product_id}`}
                            >
                                <div className="h-20 w-16 flex-shrink-0 overflow-hidden border-2 border-black bg-white">
                                    <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-heading text-lg uppercase leading-none break-words">
                                        {item.name}
                                    </p>
                                    <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.18em] text-slate-600">
                                        Size {item.size} / {item.color} × {item.quantity}
                                    </p>
                                </div>
                                <span className="shrink-0 font-mono-ui text-sm">
                                    ${(item.price * item.quantity).toFixed(2)}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 space-y-2 border-t-2 border-white pt-6 font-mono-ui text-xs uppercase tracking-[0.15em] text-white/85">
                        <Row label="Subtotal" value={`$${cartTotal.toFixed(2)}`} />
                        <Row label="Billing" value="Collected below" />
                        <Row label="Delivery" value="Confirmed manually" />
                    </div>
                    <div className="mt-4 flex items-baseline justify-between border-t-2 border-white pt-4">
                        <span className="font-mono-ui text-xs uppercase tracking-[0.2em] text-white/85">
                            Estimated Total
                        </span>
                        <span className="font-heading text-3xl text-white" data-testid="checkout-total">
                            ${cartTotal.toFixed(2)}
                        </span>
                    </div>
                </aside>
            </section>
        </div>
    );
};

const FormSection = ({ title, className = "", children }) => (
    <section className={className}>
        <h2 className="mb-4 font-mono-ui text-xs uppercase tracking-[0.25em]">
            // {title}
        </h2>
        {children}
    </section>
);

const Field = ({ label, className = "", required = true, ...props }) => (
    <label className={`block ${className}`}>
        <span className="mb-2 block font-mono-ui text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {label}
        </span>
        <input
            required={required}
            {...props}
            className="w-full border-2 border-black bg-white px-4 py-3 font-mono-ui text-sm text-black placeholder:text-slate-400 focus:border-hazard focus:outline-none"
        />
    </label>
);

const DemoField = ({ label, className = "", required = true, ...props }) => (
    <label className={`block ${className}`}>
        <span className="mb-2 block font-mono-ui text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {label}
        </span>
        <div className="flex w-full items-stretch border-2 border-black bg-white focus-within:border-hazard">
            <span
                className="inline-flex items-center border-r-2 border-black bg-white px-4 font-mono-ui text-sm text-white"
                style={{ WebkitTextStroke: "1px black" }}
            >
                
            </span>
            <input
                required={required}
                {...props}
                className="min-w-0 flex-1 bg-white px-4 py-3 font-mono-ui text-sm text-black placeholder:text-slate-400 focus:outline-none"
            />
        </div>
    </label>
);

const Row = ({ label, value }) => (
    <div className="flex justify-between">
        <span>{label}</span>
        <span>{value}</span>
    </div>
);

export default Checkout;
